import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Pause, Play, CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';

// MissingPdfsBanner — recovery affordance shown on /admin/audits when
// audits exist whose pdf_url is NULL but whose stored axe / WAVE JSON
// is intact (typical aftermath of a Browser Rendering hiccup during a
// batch run). The button kicks off a browser-side polling loop that
// fires the backfill endpoint with limit=1 once per RENDER_INTERVAL_MS,
// staying under CF's per-account new-browser rate limit.
//
// Why limit=1 + a 65s gap: empirical finding 2026-04-29 — even with
// in-endpoint pacing, CF Browser Rendering 429s within a single Pages
// Functions request. Spacing the calls to one per ~minute (across
// separate requests) is the only thing that consistently lets all
// renders succeed.

const RENDER_INTERVAL_MS = 65_000;

export default function MissingPdfsBanner() {
  const [remaining, setRemaining] = useState(null); // null = not loaded yet
  const [running, setRunning] = useState(false);
  const [secondsToNext, setSecondsToNext] = useState(0);
  const [fixedThisSession, setFixedThisSession] = useState(0);
  const [totalThisSession, setTotalThisSession] = useState(0);
  const [lastError, setLastError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const renderingRef = useRef(false);

  // Initial count fetch.
  useEffect(() => {
    let alive = true;
    api.get('/api/admin/audits/backfill-missing-pdfs')
      .then((d) => {
        if (!alive) return;
        const c = Number(d?.remaining) || 0;
        setRemaining(c);
      })
      .catch(() => { if (alive) setRemaining(0); });
    return () => { alive = false; };
  }, []);

  const stopAll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  useEffect(() => () => stopAll(), [stopAll]);

  const fireOne = useCallback(async () => {
    if (renderingRef.current) return;
    renderingRef.current = true;
    setLastError('');
    try {
      const res = await api.post('/api/admin/audits/backfill-missing-pdfs', { limit: 1, delayMs: 0 });
      const fixed = Number(res?.fixed) || 0;
      const failed = Number(res?.failed) || 0;
      const newRemaining = Number(res?.remaining) || 0;
      setRemaining(newRemaining);
      if (fixed > 0) setFixedThisSession((n) => n + fixed);
      if (failed > 0 && Array.isArray(res.errors) && res.errors.length) {
        setLastError(res.errors[0]?.error || 'render failed');
      }
      // Auto-stop + success state when everything's done.
      if (newRemaining === 0) {
        stopAll();
        setRunning(false);
        setShowSuccess(true);
        // Brief banner-flash; component caller decides when to unmount.
        setTimeout(() => setShowSuccess(false), 4000);
      }
    } catch (err) {
      setLastError(err?.message || 'request failed');
    } finally {
      renderingRef.current = false;
    }
  }, [stopAll]);

  const start = useCallback(() => {
    if (running) return;
    setRunning(true);
    setTotalThisSession(remaining || 0);
    setFixedThisSession(0);
    // Fire one immediately, then once per interval.
    fireOne();
    intervalRef.current = setInterval(fireOne, RENDER_INTERVAL_MS);
    // Countdown to next render — purely cosmetic.
    setSecondsToNext(Math.round(RENDER_INTERVAL_MS / 1000));
    countdownRef.current = setInterval(() => {
      setSecondsToNext((s) => (s <= 1 ? Math.round(RENDER_INTERVAL_MS / 1000) : s - 1));
    }, 1000);
  }, [running, remaining, fireOne]);

  const pause = useCallback(() => {
    stopAll();
    setRunning(false);
  }, [stopAll]);

  // Don't render until we've loaded the count.
  if (remaining === null) return null;

  // Success flash — brief congrats after a session finishes.
  if (showSuccess) {
    return (
      <div
        data-testid="missing-pdfs-success"
        className="mb-4 flex items-center gap-3 rounded-xl border border-[#B0FC38]/30 bg-[#B0FC38]/[0.08] px-4 py-3"
      >
        <CheckCircle2 className="w-5 h-5 text-[#B0FC38] shrink-0" />
        <div className="text-[13px] text-[#D7FF8A]">All audit PDFs restored.</div>
      </div>
    );
  }

  // Nothing to do — render nothing.
  if (remaining === 0) return null;

  // Idle (button visible) or running (status visible).
  return (
    <div
      data-testid="missing-pdfs-banner"
      className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          {!running ? (
            <>
              <div className="text-[13px] text-amber-100 font-medium">
                {remaining} audit{remaining === 1 ? '' : 's'} missing PDF{remaining === 1 ? '' : 's'}
              </div>
              <div className="text-[11.5px] text-amber-200/80 mt-0.5 leading-relaxed">
                Browser Rendering hiccupped during a batch run. The audits ran successfully but their PDFs weren't generated. Re-render from stored data (no fresh crawl, no WAVE credits). Takes ~1 minute per audit.
              </div>
            </>
          ) : (
            <>
              <div className="text-[13px] text-amber-100 font-medium">
                Re-rendering missing PDFs…
              </div>
              <div className="text-[11.5px] text-amber-200/80 mt-0.5">
                Fixed {fixedThisSession} of {totalThisSession} · {remaining} remaining · next render in 0:{String(secondsToNext).padStart(2, '0')}
              </div>
              {lastError && (
                <div className="text-[11px] text-red-300 mt-1">Last error: {lastError}</div>
              )}
            </>
          )}
        </div>
        <div className="shrink-0">
          {!running ? (
            <button
              type="button"
              onClick={start}
              data-testid="missing-pdfs-start"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-amber-500/15 border border-amber-500/30 text-amber-100 hover:bg-amber-500/25"
            >
              Re-render missing PDFs
            </button>
          ) : (
            <button
              type="button"
              onClick={pause}
              data-testid="missing-pdfs-pause"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-white/[0.04] border border-white/[0.1] text-gray-300 hover:text-white"
            >
              <Pause className="w-3 h-3" /> Pause
            </button>
          )}
        </div>
      </div>
      {!running && fixedThisSession > 0 && (
        <div className="text-[11px] text-amber-200/70 mt-2 flex items-center gap-1.5">
          <Play className="w-3 h-3" /> Resume? {fixedThisSession} already fixed this session.
        </div>
      )}
    </div>
  );
}
